/**
 * PermissionEditor — shared, accessible permission-assignment surface.
 * Used by both AddUserModal (create) and EditPermissionsModal (edit).
 *
 * Two coordinated, equivalent ways to assign each of the 37 resources:
 *  1. KANBAN BOARD — three droppable buckets (No Access / View Only / Full Access);
 *     drag a resource chip between buckets. Mouse + touch + keyboard via @dnd-kit
 *     (PointerSensor / TouchSensor / KeyboardSensor).
 *  2. CATEGORY CARDS — per-resource Full/View click toggles + per-category bulk
 *     "All Full"/"All View" + a global "Grant FULL access to everything". This is
 *     the fast, fully keyboard-operable path (the accessibility guarantee).
 *
 * Groups are derived from PERMISSION_CATEGORIES (the single source of truth in
 * permissionResources.ts) so menu/resource renames never silently drop a resource.
 */

import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Security as FullIcon,
  Visibility as ViewIcon,
  Block as NoneIcon,
  Bolt as BoltIcon,
} from '@mui/icons-material';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  closestCenter,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import {
  PERMISSION_CATEGORIES,
  PERMISSION_RESOURCES,
  getResourcesByCategory,
  PermissionResource,
  AccessLevel,
} from '../../config/permissionResources';

export type PermissionMap = Record<string, AccessLevel>;

interface ZoneDef {
  state: AccessLevel;
  label: string;
  color: string;
  icon: React.ReactNode;
}

const ZONES: ZoneDef[] = [
  { state: 'none', label: 'No Access', color: '#94A3B8', icon: <NoneIcon sx={{ fontSize: 16 }} /> },
  { state: 'view_only', label: 'View Only', color: '#6366F1', icon: <ViewIcon sx={{ fontSize: 16 }} /> },
  { state: 'full', label: 'Full Access', color: '#10B981', icon: <FullIcon sx={{ fontSize: 16 }} /> },
];

const ALL_RESOURCE_IDS = PERMISSION_RESOURCES.map((r) => r.id);

interface PermissionEditorProps {
  value: PermissionMap;
  onChange: (next: PermissionMap) => void;
}

// ---------------------------------------------------------------------------
// Draggable resource chip (lives inside a board bucket)
// ---------------------------------------------------------------------------
const ResourceChip: React.FC<{
  resource: PermissionResource;
  onRemove?: () => void;
}> = ({ resource, onRemove }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: resource.id,
    data: { resourceId: resource.id },
  });

  return (
    <Tooltip title={resource.description} arrow>
      <Chip
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        icon={<span style={{ fontSize: '13px', marginLeft: 6 }}>{resource.icon}</span>}
        label={resource.label}
        size="small"
        onDelete={onRemove}
        aria-label={`${resource.label} — drag to a different access level`}
        sx={{
          m: 0.4,
          cursor: 'grab',
          touchAction: 'none',
          bgcolor: `${resource.color}15`,
          color: resource.color,
          border: `1px solid ${resource.color}40`,
          fontWeight: 600,
          fontSize: '0.7rem',
          '&:active': { cursor: 'grabbing' },
          '&:hover': { bgcolor: `${resource.color}25` },
          opacity: isDragging ? 0.4 : 1,
          '& .MuiChip-deleteIcon': { fontSize: 15 },
        }}
      />
    </Tooltip>
  );
};

// ---------------------------------------------------------------------------
// Droppable bucket column
// ---------------------------------------------------------------------------
const BoardColumn: React.FC<{
  zone: ZoneDef;
  resources: PermissionResource[];
  onRemoveToNone?: (id: string) => void;
}> = ({ zone, resources, onRemoveToNone }) => {
  const { setNodeRef, isOver } = useDroppable({ id: zone.state, data: { state: zone.state } });

  return (
    <Box
      ref={setNodeRef}
      role="group"
      aria-label={`${zone.label} (${resources.length} resources)`}
      sx={{
        flex: 1,
        minWidth: 0,
        borderRadius: '10px',
        border: '2px dashed',
        borderColor: isOver ? zone.color : `${zone.color}40`,
        bgcolor: isOver ? `${zone.color}12` : `${zone.color}06`,
        transition: 'all 0.15s ease',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 150,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.25,
          py: 0.75,
          color: zone.color,
          borderBottom: `1px solid ${zone.color}25`,
        }}
      >
        {zone.icon}
        <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', letterSpacing: 0.3 }}>
          {zone.label}
        </Typography>
        <Chip
          size="small"
          label={resources.length}
          sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: `${zone.color}20`, color: zone.color, '& .MuiChip-label': { px: 0.75 } }}
        />
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignContent: 'flex-start', p: 0.75, flex: 1, overflowY: 'auto', maxHeight: 220 }}>
        {resources.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic', fontSize: '0.72rem', m: 1 }}>
            Drag resources here
          </Typography>
        ) : (
          resources.map((r) => (
            <ResourceChip
              key={r.id}
              resource={r}
              onRemove={zone.state !== 'none' && onRemoveToNone ? () => onRemoveToNone(r.id) : undefined}
            />
          ))
        )}
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Category card (click toggles + per-category bulk)
// ---------------------------------------------------------------------------
const CategoryCard: React.FC<{
  category: typeof PERMISSION_CATEGORIES[number];
  resources: PermissionResource[];
  stateOf: (id: string) => AccessLevel;
  setOne: (id: string, target: AccessLevel) => void;
  setMany: (ids: string[], target: AccessLevel) => void;
}> = ({ category, resources, stateOf, setOne, setMany }) => {
  const ids = resources.map((r) => r.id);
  const allFull = ids.every((id) => stateOf(id) === 'full');
  const allView = ids.every((id) => stateOf(id) === 'view_only');

  const toggle = (id: string, target: AccessLevel) => setOne(id, stateOf(id) === target ? 'none' : target);

  return (
    <Box sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: '10px', overflow: 'hidden', bgcolor: '#fff' }}>
      <Box
        sx={{
          background: `linear-gradient(135deg, ${category.color}, ${category.color}dd)`,
          color: '#fff',
          px: 1.5,
          py: 0.75,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 0.5,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', letterSpacing: 0.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {category.label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <Button
            size="small"
            onClick={() => setMany(ids, allFull ? 'none' : 'full')}
            aria-label={`Set all ${category.label} resources to full access`}
            sx={{ minWidth: 0, height: 20, fontSize: '0.6rem', fontWeight: 700, px: 0.75, color: '#fff', borderRadius: '4px', textTransform: 'none', bgcolor: allFull ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.4)' } }}
          >
            All Full
          </Button>
          <Button
            size="small"
            onClick={() => setMany(ids, allView ? 'none' : 'view_only')}
            aria-label={`Set all ${category.label} resources to view only`}
            sx={{ minWidth: 0, height: 20, fontSize: '0.6rem', fontWeight: 700, px: 0.75, color: '#fff', borderRadius: '4px', textTransform: 'none', bgcolor: allView ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.4)' } }}
          >
            All View
          </Button>
          <Chip size="small" label={ids.length} sx={{ height: 20, minWidth: 20, fontSize: '0.7rem', fontWeight: 700, bgcolor: 'rgba(255,255,255,0.25)', color: '#fff', '& .MuiChip-label': { px: 0.75 } }} />
        </Box>
      </Box>

      <Box>
        {resources.map((resource, idx) => {
          const state = stateOf(resource.id);
          return (
            <Box
              key={resource.id}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.25, py: 0.5, borderBottom: idx < resources.length - 1 ? '1px solid' : 'none', borderColor: 'grey.100', '&:hover': { bgcolor: 'grey.50' } }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: '12px', flexShrink: 0 }}>{resource.icon}</Typography>
                <Typography title={resource.description} sx={{ fontSize: '0.73rem', fontWeight: 500, color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {resource.label}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, ml: 0.5 }}>
                <Button
                  size="small"
                  variant={state === 'full' ? 'contained' : 'outlined'}
                  onClick={() => toggle(resource.id, 'full')}
                  aria-pressed={state === 'full'}
                  aria-label={`${resource.label}: full access`}
                  sx={{ minWidth: 38, height: 22, fontSize: '0.65rem', fontWeight: 700, px: 0.75, borderRadius: '4px', textTransform: 'none', ...(state === 'full' ? { bgcolor: '#10B981', color: '#fff', '&:hover': { bgcolor: '#059669' } } : { borderColor: 'grey.300', color: 'grey.500', '&:hover': { borderColor: '#10B981', color: '#10B981', bgcolor: '#10B98108' } }) }}
                >
                  Full
                </Button>
                <Button
                  size="small"
                  variant={state === 'view_only' ? 'contained' : 'outlined'}
                  onClick={() => toggle(resource.id, 'view_only')}
                  aria-pressed={state === 'view_only'}
                  aria-label={`${resource.label}: view only`}
                  sx={{ minWidth: 38, height: 22, fontSize: '0.65rem', fontWeight: 700, px: 0.75, borderRadius: '4px', textTransform: 'none', ...(state === 'view_only' ? { bgcolor: '#6366F1', color: '#fff', '&:hover': { bgcolor: '#4F46E5' } } : { borderColor: 'grey.300', color: 'grey.500', '&:hover': { borderColor: '#6366F1', color: '#6366F1', bgcolor: '#6366F108' } }) }}
                >
                  View
                </Button>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main editor
// ---------------------------------------------------------------------------
export const PermissionEditor: React.FC<PermissionEditorProps> = ({ value, onChange }) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const stateOf = (id: string): AccessLevel => value[id] ?? 'none';

  const setOne = (id: string, target: AccessLevel) => onChange({ ...value, [id]: target });
  const setMany = (ids: string[], target: AccessLevel) => {
    const next = { ...value };
    ids.forEach((id) => { next[id] = target; });
    onChange(next);
  };

  const fullCount = ALL_RESOURCE_IDS.filter((id) => stateOf(id) === 'full').length;
  const viewCount = ALL_RESOURCE_IDS.filter((id) => stateOf(id) === 'view_only').length;
  const noneCount = ALL_RESOURCE_IDS.length - fullCount - viewCount;
  const everythingFull = fullCount === ALL_RESOURCE_IDS.length;

  // Resources grouped per board bucket
  const byZone = useMemo(() => {
    const buckets: Record<AccessLevel, PermissionResource[]> = { none: [], view_only: [], full: [] };
    PERMISSION_RESOURCES.forEach((r) => { buckets[stateOf(r.id)].push(r); });
    return buckets;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleGrantAll = () => {
    if (everythingFull) {
      setConfirmClearOpen(true);
    } else {
      setMany(ALL_RESOURCE_IDS, 'full');
    }
  };

  const handleDragStart = (e: DragStartEvent) => setActiveId(String(e.active.id));
  const handleDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    const overState = e.over?.data?.current?.state as AccessLevel | undefined;
    const resourceId = (e.active.data?.current?.resourceId as string) ?? String(e.active.id);
    if (overState && resourceId && stateOf(resourceId) !== overState) {
      setOne(resourceId, overState);
    }
  };

  const activeResource = activeId ? PERMISSION_RESOURCES.find((r) => r.id === activeId) : null;

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      {/* Action bar + live summary */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <Button
          variant={everythingFull ? 'outlined' : 'contained'}
          startIcon={<BoltIcon />}
          onClick={handleGrantAll}
          sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.78rem', ...(everythingFull ? { borderColor: '#10B981', color: '#10B981' } : { background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', color: '#fff' }) }}
        >
          {everythingFull ? `Full access to all ${ALL_RESOURCE_IDS.length} — click to clear` : `Grant FULL access to everything (${ALL_RESOURCE_IDS.length})`}
        </Button>
        <Typography aria-live="polite" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
          <Box component="span" sx={{ color: '#10B981', fontWeight: 700 }}>{fullCount} full</Box>{' · '}
          <Box component="span" sx={{ color: '#6366F1', fontWeight: 700 }}>{viewCount} view</Box>{' · '}
          {noneCount} none — of {ALL_RESOURCE_IDS.length}
        </Typography>
      </Box>

      {/* Kanban board */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
        {ZONES.map((zone) => (
          <BoardColumn key={zone.state} zone={zone} resources={byZone[zone.state]} onRemoveToNone={(id) => setOne(id, 'none')} />
        ))}
      </Box>

      {/* Category cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' }, gap: 1.25 }}>
        {PERMISSION_CATEGORIES.map((category) => {
          const resources = getResourcesByCategory(category.id);
          if (resources.length === 0) return null;
          return (
            <CategoryCard key={category.id} category={category} resources={resources} stateOf={stateOf} setOne={setOne} setMany={setMany} />
          );
        })}
      </Box>

      <DragOverlay>
        {activeResource ? (
          <Chip
            icon={<span style={{ fontSize: '13px', marginLeft: 6 }}>{activeResource.icon}</span>}
            label={activeResource.label}
            size="small"
            sx={{ bgcolor: `${activeResource.color}25`, color: activeResource.color, border: `1px solid ${activeResource.color}`, fontWeight: 700, fontSize: '0.7rem', boxShadow: 3, cursor: 'grabbing' }}
          />
        ) : null}
      </DragOverlay>

      {/* Confirm clear-all */}
      <Dialog open={confirmClearOpen} onClose={() => setConfirmClearOpen(false)}>
        <DialogTitle>Clear all permissions?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes access to all {ALL_RESOURCE_IDS.length} resources (sets everything to No Access). You can re-assign afterward.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClearOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => { setMany(ALL_RESOURCE_IDS, 'none'); setConfirmClearOpen(false); }}>
            Clear all
          </Button>
        </DialogActions>
      </Dialog>
    </DndContext>
  );
};

// Convert between the editor's map form and the API's Permission[] form.
export const permissionsArrayToMap = (perms: { resource: string; access_level: AccessLevel }[] | undefined): PermissionMap => {
  const map: PermissionMap = {};
  PERMISSION_RESOURCES.forEach((r) => { map[r.id] = 'none'; });
  (perms || []).forEach((p) => { map[p.resource] = p.access_level; });
  return map;
};

export const mapToPermissionsArray = (map: PermissionMap): { resource: string; access_level: AccessLevel }[] =>
  Object.entries(map)
    .filter(([, level]) => level !== 'none')
    .map(([resource, access_level]) => ({ resource, access_level }));

export default PermissionEditor;
